import { queryClient } from "@/api/client"
import { type DenormalizedTemplate, getViewDataTemplates } from "@/api/templates"
import { getViewDataWorkspaces } from "@/api/workspaces"
import { queryKeys } from "@/queries/keys"
import { listsFromWorkspaces } from "@/queries/lists"
import { toWorkspace } from "@/queries/workspaces"
import { unwrapOrThrow } from "@/util/result"
import { logEvent } from "@/util/telemetry"
import { viewManager } from "@/util/viewManager"

export type DeferredViewDataKey = "templates" | "workspaces"

/**
 * Fetches a deferred viewData key from the API and writes it into its read
 * source. React Query owns the loading/error/dedup lifecycle around these
 * fetchers. The map is keyed by `DeferredViewDataKey`, so adding a key to the
 * union without wiring its fetch path is a compile error.
 */
export const deferredViewDataFetchers = {
  templates: async (): Promise<DenormalizedTemplate[]> => {
    const { data: templates } = await getViewDataTemplates()
    return templates
  },
  workspaces: async (): Promise<true> => {
    const workspaces = unwrapOrThrow(await getViewDataWorkspaces())
    // Workspaces are read from the `workspaces.all` cache; the lists nested in
    // the same payload seed the `lists.all` cache.
    queryClient.setQueryData(queryKeys.workspaces.all, workspaces.map(toWorkspace))
    queryClient.setQueryData(queryKeys.lists.all, listsFromWorkspaces(workspaces))
    return true
  },
} satisfies Record<DeferredViewDataKey, () => Promise<unknown>>

/** React Query cache key that drives each deferred key's fetch/loading state. */
export const deferredViewDataQueryKeys: Record<DeferredViewDataKey, readonly unknown[]> =
  {
    templates: queryKeys.templates.all,
    workspaces: queryKeys.workspaces.viewData,
  }

// Cache the parsed deferredKeys list so every hook invocation doesn't re-read.
const deferredKeys: ReadonlySet<DeferredViewDataKey> = new Set(
  ((viewManager.get("deferredKeys") as DeferredViewDataKey[] | undefined) ?? []).filter(
    (key) => {
      const hasHandler = key in deferredViewDataFetchers
      if (!hasHandler) {
        // Deploy skew: the server declared a key this client build has no
        // handler for. Degrade gracefully instead of hanging forever.
        logEvent("error", "No client handler for deferred viewData key", {
          capture: true,
          error: new Error(`Unhandled deferred viewData key: ${key}`),
        })
      }
      return hasHandler
    },
  ),
)

/** Whether a key was deferred by the server out of the inlined viewData blob. */
export function isDeferredKey(key: DeferredViewDataKey): boolean {
  return deferredKeys.has(key)
}
